import type { HandoverAgent, Party } from '@acasa/shared'
import { useId } from 'react'
import { CONTROL } from './fields'

/**
 * The Makler list from Einstellungen, offered on a Makler card.
 *
 * Choosing an entry is an explicit instruction, so it OVERWRITES all four
 * fields — the same rule as "Übernehmen" beside one previous reading. Nothing
 * is stored about the choice: the fields are ordinary fields afterwards, and
 * a Makler who is not in the list can still be typed in.
 *
 * The displayed value is COMPUTED: the entry whose four values the party
 * carries, else the placeholder. A stored selection would go on naming
 * somebody after their telephone number was corrected by hand.
 */
export function AgentPicker({
  agents,
  party,
  disabled,
  onTake,
}: {
  agents: HandoverAgent[]
  party: Party
  disabled?: boolean
  onTake: (agent: HandoverAgent) => void
}) {
  const id = useId()
  if (agents.length === 0) return null

  const same = (left: string, right: string) => left.trim() === right.trim()
  const current = agents.find(
    (agent) =>
      same(agent.name, party.name) &&
      same(agent.address, party.address) &&
      same(agent.phone, party.phone) &&
      same(agent.email, party.email),
  )

  return (
    <div className="mb-3">
      <label htmlFor={id} className="text-muted mb-1 block text-[11px] tracking-wide uppercase">
        Aus den Einstellungen übernehmen
      </label>
      <select
        id={id}
        className={`${CONTROL} min-h-11`}
        disabled={disabled}
        value={current?.id ?? ''}
        onChange={(event) => {
          const chosen = agents.find((agent) => agent.id === event.target.value)
          if (chosen) onTake(chosen)
        }}
      >
        <option value="" disabled>
          Makler wählen …
        </option>
        {agents.map((agent) => {
          const hint = agent.address || agent.phone
          return (
            <option key={agent.id} value={agent.id}>
              {hint ? `${agent.name} — ${hint}` : agent.name}
            </option>
          )
        })}
      </select>
    </div>
  )
}
